








































































































































































































import Vue from 'vue';
import { NoExpression, ObjectType, Expression, Program, ProgramDataSet, Entity, DataTypes } from 'expangine-runtime';
import { TypeUpdateEvent, TypeSettings } from '../runtime/types/TypeVisuals';
import { Preferences, PreferenceCategory } from './Preference';
import { LiveRuntime } from 'expangine-runtime-live';
import { getConfirmation } from './Confirm';
import { Registry } from '../runtime/Registry';
import { System } from './SystemEvents';
import { ExplorerTab } from './explorer/ExplorerTypes';
import { Debugger, DebuggerOptions } from './Debugger';
import { now } from './StopWatch';
import { getInput } from './Input';
import { sendNotification } from './Notify';


const PREF_REMOVE_PROGRAM = Preferences.define({
  key: 'program_remove',
  label: 'Remove program without confirmation',
  category: [PreferenceCategory.CONFIRM],
  defaultValue: false,
});

const PREF_REMOVE_DATASET = Preferences.define({
  key: 'program_dataset_remove',
  label: 'Remove program dataset without confirmation',
  category: [PreferenceCategory.CONFIRM],
  defaultValue: false,
});

export default Vue.extend({
  props: {
    program: {
      type: Object as () => Program,
      required: true,
    },
    registry: {
      type: Object as () => Registry,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    hideName: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    id: Entity.uuid(),
    tab: 0,
    debug: null as null | Debugger,
    dataIndex: 0,
  }),
  computed: {
    settings(): TypeSettings {
      if (this.registry.getTypeSettingsValidFor(this.program.dataType, this.program.meta)) {
        return this.program.meta;
      }
      
      return this.program.meta = this.registry.getTypeSettings(this.program.dataType);
    },
    currentData(): any {
      return this.program.datasets[this.dataIndex].data;
    },
    isDebugVisible(): boolean {
      return this.tab === 4 && !!this.debug;
    },
    isRunVisible(): boolean {
      return this.tab === 3;
    },
    isSingleDataset(): boolean {
      return this.program.datasets.length === 1;
    },
  },
  methods: {
    renamed(newName: string) {
      const { registry, program } = this;
      const { defs } = registry;

      if (program.name) {
        if (defs.renameProgram(program.name, newName)) {
          this.$emit('renamed', this.program);
        } else {
          return;
        }
      } else {
        program.name = newName;

        defs.addProgram(program);
      }

      this.markUpdated();
    },
    validateName(name: string) {
      if (!name) {
        return 'A name is required.';
      }

      const existing = this.registry.defs.getProgram(name);
      if (existing && existing !== this.program) {
        return 'A function already exists with that name.';
      }

      return '';
    },
    async remove() {
      if (!await getConfirmation({ message: `Are you sure you want to remove this program?`, pref: PREF_REMOVE_PROGRAM })) {
        return;
      }

      this.registry.defs.removeProgram(this.program);

      this.$emit('removed', this.program);
    },
    onDebugSetup() {
      const { registry, program: { dataType: inputType, datasets, expression: program } } = this;
      const options: DebuggerOptions = {
        registry,
        inputType,
        input: datasets[this.dataIndex].data,
        program,
      };

      if (this.debug) {
        this.debug.set(options);
      } else {
        this.debug = new Debugger(options);
      }
    },
    onChange(event: TypeUpdateEvent) {
      this.program.dataType = event.type as ObjectType;
      this.program.meta = event.settings;

      if (event.transform) {
        this.program.refactor(event.transform, LiveRuntime);
      }

      this.markUpdated();
    },
    onProgramChange(program: Expression) {
      this.program.expression = program;
      this.markUpdated();
    },
    onProgramRemove() {
      this.program.expression = NoExpression.instance;
      this.markUpdated();
    },
    onDataChange(data: any) {
      this.program.updateDataset(this.dataIndex, {
        data,
        updated: now(),
      });
      this.markUpdated();
    },
    markUpdated(): void {
      this.program.changed();

      this.$emit('updated', this.program);
    },
    run() {
      const { program, registry } = this;
      const tab: ExplorerTab = {
        id: `run_${this.id}`,
        name: `Run ${this.program.name}`,
        icon: 'mdi-play',
        component: 'ex-run-program',
        bind: { registry, program },
        on: {},
        close: () => { /**/ },
      };

      System.forkTab(tab);
    },
    addDataset() {
      this.program.addDataset({
        name: 'Data Set #' + (this.program.datasets.length + 1),
        data: this.program.dataType.create(),
        created: now(),
        updated: now(),
        meta: null,
      });
      this.markUpdated();
    },
    async setPrimaryDataSet(dataset: ProgramDataSet) {      
      this.program.moveDataset(dataset, 0);
      this.dataIndex = 0;
      this.markUpdated();
    },
    async copyDataset(dataset: ProgramDataSet) {

      const name = await getInput({
        message: `Dataset name for copy of ${dataset.name}`,
        value: `${dataset.name} Copy`,
      });

      if (!name) {
        return sendNotification({ message: 'Dataset copy cancelled.' });
      }

      if (this.getDatasetByName(name)) {
        return sendNotification({ message: 'A dataset with that name already exists.' });
      }

      const copy: ProgramDataSet = {
        name,
        data: DataTypes.copy(dataset.data),
        created: now(),
        updated: now(),
        meta: DataTypes.copy(dataset.meta),
      };

      this.program.addDataset(copy);
      this.dataIndex = this.program.datasets.length - 1;
      this.markUpdated();
    },
    async renameDataset(dataset: ProgramDataSet) {
      const name = await getInput({
        message: `Dataset Name`,
        value: `${dataset.name}`,
      });

      if (!name) {
        return sendNotification({ message: 'Dataset rename cancelled.' });
      }

      if (this.getDatasetByName(name)) {
        return sendNotification({ message: 'A dataset with that name already exists.' });
      }

      this.program.updateDataset(dataset, { name });
      this.markUpdated();
    },
    async removeDataset(dataset: ProgramDataSet) {
      if (!await getConfirmation({ message: `Are you sure you want to remove this dataset?`, pref: PREF_REMOVE_DATASET })) {
        return;
      }

      this.program.removeDataset(dataset);

      if (this.dataIndex >= this.program.datasets.length) {
        this.dataIndex = this.program.datasets.length - 1;
      }
      this.markUpdated();
    },
    getDatasetByName(name: string): ProgramDataSet | undefined {
      return this.program.datasets.find((d) => d.name === name);
    },
  },
});
